import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import {
	pdfWorkerPath,
	pdfcmaps,
	startPath,
	pageNumber,
} from "./app-signals.js";
import { signal, effect } from "alien-signals";
import { pageCount, pdfSize } from "./page-signals.js";
import { defaultPdfAssets } from "./pdf-assets.js";
export { TextLayer } from "pdfjs-dist/build/pdf.mjs";

export const pdfDocument = signal();
export const pdfOutline = signal([]);
export const pdfMetadata = signal({});
export const pdfLoadingTask = signal();
export const pdfLoadingError = signal();
export const pdfLoadingProgress = signal({ percent: 0 });
export const pdfReady = signal(false);

function deferred() {
	let resolve, reject;
	const promise = new Promise((res, rej) => {
		resolve = res;
		reject = rej;
	});
	// Callers may never await a load (the reader itself does not), so a
	// failure must not surface as an unhandled rejection; it is reported
	// through pdfLoadingError instead.
	promise.catch(() => {});
	return { promise, resolve, reject, settled: false };
}

// Each load gets a generation number. Work from a load that has since been
// superseded is dropped instead of writing into the newer load's state,
// and its completion promise follows the newer load rather than hanging.
let loadGeneration = 0;
let currentLoad = deferred();
export const pdfLoadingComplete = signal(currentLoad.promise);

function settle(load, outcome, value) {
	if (load.settled) return;
	load.settled = true;
	load[outcome](value);
}

/**
 * Loads the PDF at `url` and resolves with the PDFDocumentProxy once the
 * document, its outline and its metadata are ready. Rejects when the
 * document cannot be loaded; pdfLoadingError is then set too.
 */
export function loadPDF(url) {
	startPath(url);
	return pdfLoadingComplete();
}

async function loadOutline(pdf) {
	try {
		return await processOutline((await pdf.getOutline()) || []);
		/* c8 ignore next 4 */
	} catch (err) {
		// A broken outline must not make the pages unreadable.
		console.error(err);
		return [];
	}
}

async function loadMetadata(pdf) {
	try {
		return await pdf.getMetadata();
		/* c8 ignore next 4 */
	} catch (err) {
		console.error(err);
		return {};
	}
}

// The loading task of the latest load. Kept outside the signals because
// openDocument runs inside an effect, where reading a signal it also
// writes would subscribe the effect to it and loop.
let activeTask;

async function openDocument({ url, workerSrc, cMapUrl }) {
	const generation = ++loadGeneration;
	const load = deferred();
	settle(currentLoad, "resolve", load.promise);
	currentLoad = load;
	pdfLoadingComplete(load.promise);

	// Cancels a load still in flight and frees the previous document's
	// worker; nothing will render from it again.
	activeTask?.destroy();
	pdfReady(false);
	pdfLoadingError(undefined);
	pdfLoadingProgress({ percent: 0 });
	pdfOutline([]);
	pdfMetadata({});
	pdfDocument(undefined);

	pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
	const task = pdfjsLib.getDocument({
		url,
		cMapUrl,
		cMapPacked: true,
		// Fonts for PDFs that do not embed the standard 14, decoders for
		// JPEG 2000 and JBIG2 images, and the CMYK colour profile. Without
		// them those images do not render at all.
		standardFontDataUrl: defaultPdfAssets.standardFontDataUrl,
		wasmUrl: defaultPdfAssets.wasmUrl,
		iccUrl: defaultPdfAssets.iccUrl,
		enableXfa: true,
	});
	activeTask = task;
	pdfLoadingTask(task);
	const current = () => generation === loadGeneration;
	task.onProgress = (progress) => {
		if (current()) pdfLoadingProgress(progress);
	};

	try {
		const pdf = await task.promise;
		if (!current()) return;
		const count = pdf.numPages;
		// A saved or configured start page may be past the end of this
		// document (for example a shorter replacement sample).
		const startPage = Math.min(Math.max(pageNumber() || 1, 1), count);
		const page = await pdf.getPage(startPage);
		if (!current()) return;
		pdfSize(page.getViewport({ scale: 1.0 }));
		pageCount(count);
		if (pageNumber() !== startPage) pageNumber(startPage);
		pdfDocument(pdf);
		const [outline, metadata] = await Promise.all([
			loadOutline(pdf),
			loadMetadata(pdf),
		]);
		if (!current()) return;
		pdfOutline(outline);
		pdfMetadata(metadata);
		pdfReady(true);
		settle(load, "resolve", pdf);
	} catch (err) {
		if (!current()) return;
		console.error(err);
		pdfLoadingError(err);
		settle(load, "reject", err);
	}
}

effect(() => {
	const url = startPath();
	const workerSrc = pdfWorkerPath();
	const cMapUrl = pdfcmaps();
	if (!url || !workerSrc || !cMapUrl) return;
	openDocument({ url, workerSrc, cMapUrl });
});

export async function getDestinationPageNumber(destination) {
	const pdf = pdfDocument();
	if (!pdf) return;
	// This function should be used for link render to ensure that internal links work properly.
	if (typeof destination === "string") {
		destination = await pdf.getDestination(destination);
	}
	if (!destination) return null;
	const [ref] = destination;
	let pageNumber;
	if (ref && typeof ref === "object") {
		try {
			pageNumber = (await pdf.getPageIndex(ref)) + 1;
		} catch {
			console.error(
				`getDestinationPageNumber: "${ref}" is not a valid page reference, for dest="${destination}".`,
			);
			return null;
		}
	} else if (Number.isInteger(ref)) {
		pageNumber = ref + 1;
	}

	return pageNumber || null;
}

export async function processOutline(outline) {
	const outlineArray = outline.map(async (item) => {
		const { dest, items } = item;
		let processedItems;
		const pageNumber = await getDestinationPageNumber(dest);
		if (items) {
			processedItems = await processOutline(items);
		}
		return Object.assign({}, item, { pageNumber, items: processedItems });
	});
	return Promise.all(outlineArray);
}
